import invariant from 'invariant';
import { identity, max, times } from 'lodash';

import { maxBy } from '@mythos/utils/utils';
import nullthrows from 'nullthrows';
import {
  createRngFromState,
  getInitialRngState,
  RandomGenerator,
  randomInt,
} from '../Random';
import * as GameConstants from './GameConstants';
import { nextPhase, Phase, PhaseOrder } from './Phases';
import * as Rules from './Rules';
import * as Utility from './Utility';

import { match } from 'ts-pattern';
import { generateSeed, randomSelect, shuffle } from '../Random';
import { CardAffinity } from './CardTypes';
import { ActionTypes, GameAction } from './GameActions';
import {
  ConflictRoundInfo,
  EventTypes,
  GameEvent,
  GameEventData,
} from './GameEvents';
import {
  applyGameEvents,
  applyGameEventsMutable,
  CardDef,
  ClashResults,
  GameComputedState,
  GamePlayerInfo,
  GameRatingDelta,
  GameSecretState,
  GameServerState,
  GameStaticState,
  GameType,
  getPlayerState,
  initialGameComputedState,
  initialGameSecretState,
  initialGameServerState,
  mergeModuleCards,
  ModuleDef,
  PlayerWarInfo,
  TributeDef,
} from './GameModel';
import { GameOptions, NoDiceType } from './GameOptions';
import { Token } from './Resources';
import { getCurrentTurnEvents, getTributeLogic } from './TributeLogic';

import { getMaxCounters } from './Rules';
import {
  getTutorialData,
  TUTORIAL_CARD_ORDER_BY_AGE,
  TUTORIAL_TRIBUTE_ORDER,
} from './Tutorial';

export const GAME_VERSION = 2;
// Version history:
// v1: Original format with GAIN_LEADER events
// v2: GAIN_LEADER replaced by ASSIGN_LEADER + GAIN_CARD
export const UNINITIALIZED_SEQUENCE_ID = -1;

export type RenderedGame = {
  id: string;
  version: number;
  type: GameType;
  options: GameOptions;
  boardSize: number;
  tributeRowSize: number;
  turnsPerAge: number;
  cardDefinitionsByID: { [key: string]: CardDef };
  tributeCardDefinitionsByID: { [key: string]: TributeDef };
  playerInfos: GamePlayerInfo[];

  sequenceID: number;
  gameStartTimestamp: number;
  gameEndTimestamp: number | null;
  phaseStartTimestamp: number;
  readyByUserID: { [key: string]: boolean };
  totalThinkingMillisByPlayerID: { [key: string]: number };
  phaseThinkingMillisByPlayerID: { [key: string]: number };

  events: GameEvent[];

  ratingDeltas: GameRatingDelta[] | null;

  age: number;
  turn: number;
  phase: Phase;
  winningPlayerIDs: string[] | null;

  tableIDs: string[];
  tributeRowIDs: string[];
  basicPileIDs: string[];

  deckIDs: string[];
  discardIDs: string[];
  tributeDeckIDs: string[];
  tributeDiscardIDs: string[];

  cardDefIDByID: { [key: string]: string };
  tributeCardDefIDByID: { [key: string]: string };

  playerStates: RenderedPlayer[];
  planningStateByTributeID: { [tributeID: string]: any };
} & RenderedSecretState;
export type RenderedSecretState = {
  bidsByPlayerID: { [key: string]: Rules.Bid };
  resolvesByPlayerID: { [key: string]: Rules.ResolutionSelection };
};
export type RenderedPlayer = {
  userID: string;
  cardIDs: string[];
  counters: Utility.CounterDelta;
  bid: Rules.Bid | null;
  selectedCardID: string | null;
};

export default class Game {
  staticState: GameStaticState;
  serverState: GameServerState;
  secretState: GameSecretState;
  computedState: GameComputedState;

  sequenceID: number;

  fetchedSequenceID: number = UNINITIALIZED_SEQUENCE_ID;
  fetchedEventsLength: number = 0;

  private _rng: RandomGenerator;

  private _heldPhase: Phase | null = null;

  private constructor(
    staticState: GameStaticState,
    serverState: GameServerState,
    secretState: GameSecretState,
    computedState: GameComputedState,
    sequenceID: number,
  ) {
    this.staticState = staticState;
    this.serverState = serverState;
    this.secretState = secretState;
    this.computedState = computedState;

    this.sequenceID = sequenceID;

    this._rng = createRngFromState(this.secretState.rngState);
  }

  static newGame(
    id: string,
    playerInfos: GamePlayerInfo[],
    options: GameOptions,
    coreCardDefinitions: CardDef[],
    coreTributeCardDefinitions: TributeDef[],
    modules: ModuleDef[],
  ): Game {
    const {
      cardDefinitions: rawCardDefinitions,
      tributeCardDefinitions: rawTributeCardDefinitions,
    } = mergeModuleCards(
      coreCardDefinitions,
      coreTributeCardDefinitions,
      modules,
    );
    modules.forEach((module) => {
      options = {
        ...options,
        ...module.gameOptionsToApply,
      };
    });

    const cardDefinitionsByID = Object.fromEntries(
      rawCardDefinitions.map((card_def) => {
        return [
          card_def.defID,
          {
            ...card_def,
          },
        ];
      }),
    );
    const tributeCardDefinitionsByID = Object.fromEntries(
      rawTributeCardDefinitions.map((card_def) => {
        return [
          card_def.defID,
          {
            ...card_def,
          },
        ];
      }),
    );

    let boardSize = playerInfos.length + 1;
    if (options.biggerTradeRow) {
      boardSize += 1;
    }
    const tributeRowSize = 3;

    const staticState: GameStaticState = {
      id,
      version: GAME_VERSION,
      type: options.tutorial ? 'tutorial' : 'casual',
      playerInfos,
      options,

      cardDefinitionsByID,
      tributeCardDefinitionsByID,
      boardSize,
      tributeRowSize,
      turnsPerAge: GameConstants.TURNS_PER_AGE,
    };
    const serverState: GameServerState = initialGameServerState(staticState);
    const secretState: GameSecretState = initialGameSecretState(
      getInitialRngState(generateSeed()),
    );
    const computedState: GameComputedState =
      initialGameComputedState(staticState);

    if (options.tutorial) {
      secretState.tutorialState = {
        nextCardIndex: 0,
        nextTributeIndex: 0,
      };
    }

    const game = new Game(
      staticState,
      serverState,
      secretState,
      computedState,
      0,
    );
    game.setUpGame();

    return game;
  }

  static fromSerialized(
    sequenceID: number,
    staticState: GameStaticState,
    serverState: GameServerState,
    secretState: GameSecretState,
  ): Game {
    if (staticState.version !== GAME_VERSION) {
      throw new Error(
        `Game.fromSerialized: version mismatch (got ${staticState.version}, expected ${GAME_VERSION})`,
      );
    }

    const computedState = initialGameComputedState(staticState);
    applyGameEventsMutable(computedState, serverState.events);

    const game = new Game(
      staticState,
      serverState,
      secretState,
      computedState,
      sequenceID,
    );
    game.fetchedSequenceID = sequenceID;
    game.fetchedEventsLength = serverState.events.length;

    return game;
  }

  public isFinished(): boolean {
    return this.serverState.gameEndTimestamp !== null;
  }

  public addAction(userID: string, action: GameAction): void {
    if (this.isFinished()) {
      throw new Error('game is finished');
    }

    match(action)
      .with({ type: ActionTypes.BID }, ({ bid }) => {
        if (this.secretState.bidsByPlayerID[userID]) {
          throw new Error('bid already submitted');
        }

        Rules.validateBid(
          getPlayerState(this.computedState, userID),
          bid,
          this.computedState,
        );

        this.secretState.bidsByPlayerID[userID] = bid;
        this.serverState.phaseThinkingMillisByPlayerID[userID] =
          Date.now() - this.serverState.phaseStartTimestamp;
      })
      .with({ type: ActionTypes.UNBID }, () => {
        if (!this.staticState.options.unbid) {
          throw new Error('unbid is not enabled');
        }
        if (this.computedState.phase !== Phase.PLANNING) {
          throw new Error('cannot unbid after planning phase');
        }
        if (!this.secretState.bidsByPlayerID[userID]) {
          throw new Error('no bid to unbid');
        }

        delete this.secretState.bidsByPlayerID[userID];
        delete this.serverState.phaseThinkingMillisByPlayerID[userID];
      })
      .with({ type: ActionTypes.RESOLVE }, ({ resolve }) => {
        if (this.secretState.resolvesByPlayerID[userID]) {
          throw new Error('resolve already submitted');
        }

        Rules.validateResolve(
          getPlayerState(this.computedState, userID),
          resolve,
          this.computedState,
        );

        this.secretState.resolvesByPlayerID[userID] = resolve;
        this.serverState.phaseThinkingMillisByPlayerID[userID] =
          Date.now() - this.serverState.phaseStartTimestamp;
      })
      .exhaustive();

    this.progressIfPossible();
    this.sequenceID += 1;
    this.serverState.gameUpdateTimestamp = Date.now();

    this.updateReadyByUserID();
  }

  public renderSecretState(viewerID: string | null): RenderedSecretState {
    let bidsByPlayerID: { [key: string]: Rules.Bid } = {};
    let resolvesByPlayerID: { [key: string]: Rules.ResolutionSelection } = {};

    if (viewerID) {
      if (this.secretState.bidsByPlayerID[viewerID]) {
        bidsByPlayerID[viewerID] = this.secretState.bidsByPlayerID[viewerID];
      }
      if (this.secretState.resolvesByPlayerID[viewerID]) {
        resolvesByPlayerID[viewerID] =
          this.secretState.resolvesByPlayerID[viewerID];
      }
    }
    return {
      bidsByPlayerID,
      resolvesByPlayerID,
    };
  }

  public render(viewerID: string | null): RenderedGame {
    const secretState = this.renderSecretState(viewerID);

    return {
      // static state
      id: this.staticState.id,
      version: this.staticState.version,
      type: this.staticState.type,
      options: this.staticState.options,
      boardSize: this.staticState.boardSize,
      tributeRowSize: this.staticState.tributeRowSize,
      turnsPerAge: this.staticState.turnsPerAge,

      cardDefinitionsByID: this.staticState.cardDefinitionsByID,
      tributeCardDefinitionsByID: this.staticState.tributeCardDefinitionsByID,

      playerInfos: this.staticState.playerInfos,

      // server state
      sequenceID: this.sequenceID,
      gameStartTimestamp: this.serverState.gameStartTimestamp,
      gameEndTimestamp: this.serverState.gameEndTimestamp,
      ratingDeltas: this.serverState.ratingDeltas,
      phaseStartTimestamp: this.serverState.phaseStartTimestamp,
      readyByUserID: this.serverState.readyByUserID,
      totalThinkingMillisByPlayerID:
        this.serverState.totalThinkingMillisByPlayerID,
      phaseThinkingMillisByPlayerID:
        this.serverState.phaseThinkingMillisByPlayerID,

      // rendered secret state
      ...secretState,

      // computed state
      age: this.computedState.age,
      turn: this.computedState.turn,
      phase: this.computedState.phase,
      winningPlayerIDs: this.computedState.winningPlayerIDs,

      tableIDs: this.computedState.table.map((card) => card.id),
      tributeRowIDs: this.computedState.tributeRow.map((card) => card.id),
      basicPileIDs: this.computedState.basicPile.map((card) => card.id),

      deckIDs: this.computedState.deck.map((card) => card.id),
      discardIDs: this.computedState.discard.map((card) => card.id),
      tributeDeckIDs: this.computedState.tributeDeck.map((card) => card.id),
      tributeDiscardIDs: this.computedState.tributeDiscard.map(
        (card) => card.id,
      ),

      cardDefIDByID: Object.fromEntries(
        Object.entries(this.computedState.cardsByID).map(([id, card]) => [
          id,
          card.defID,
        ]),
      ),
      tributeCardDefIDByID: Object.fromEntries(
        Object.entries(this.computedState.tributeCardsByID).map(
          ([id, card]) => [id, card.defID],
        ),
      ),

      playerStates: this.computedState.playerStates.map((playerState) => {
        return {
          userID: playerState.userID,
          cardIDs: playerState.cards.map((card) => card.id),
          counters: playerState.counters,
          bid: playerState.bid,
          selectedCardID: playerState.selectedCard?.id ?? null,
        };
      }),
      planningStateByTributeID: this.computedState.planningStateByTributeID,
      events: this.computedState.events,
    };
  }

  // Methods for testing
  _debugHoldOnPhase(phase: Phase | null): void {
    this._heldPhase = phase;
  }

  _debugAddEvents(events: GameEventData[]): void {
    this._addEvents(events);
  }

  _debugProgressIfPossible(): void {
    this.progressIfPossible();
  }

  private updateReadyByUserID(): void {
    if (this.computedState.phase === Phase.PLANNING) {
      this.staticState.playerInfos.forEach((playerInfo) => {
        this.serverState.readyByUserID[playerInfo.userID] =
          !!this.secretState.bidsByPlayerID[playerInfo.userID];
      });
    } else if (this.computedState.phase === Phase.RESOLUTION) {
      this.staticState.playerInfos.forEach((playerInfo) => {
        this.serverState.readyByUserID[playerInfo.userID] =
          !!this.secretState.resolvesByPlayerID[playerInfo.userID];
      });
    } else {
      this.staticState.playerInfos.forEach((playerInfo) => {
        this.serverState.readyByUserID[playerInfo.userID] = true;
      });
    }
  }

  private _shuffle<T>(array: T[]): T[] {
    const [shuffled, rng] = shuffle(array, this._rng);
    this._setRNG(rng);
    return shuffled;
  }
  private _randomSelect<T>(array: T[]): T {
    const [t, rng] = randomSelect(array, this._rng);
    this._setRNG(rng);
    return t;
  }
  private _rollDie(): number {
    const [v, rng] = randomInt(1, 6, this._rng);
    this._setRNG(rng);
    return v;
  }

  private _setRNG(rng: RandomGenerator): void {
    this._rng = rng;
    this.secretState.rngState = rng.getState();
  }

  private setUpGame() {
    const { playerInfos, cardDefinitionsByID, options } = this.staticState;
    // leaders
    let validLeaders = Object.values(cardDefinitionsByID).filter(
      (card_def) => card_def.affinity === CardAffinity.Leader,
    );
    const orderedLeaders = match(options.randomLeader)
      .with(true, () => {
        invariant(
          validLeaders.length >= playerInfos.length,
          'Not enough leaders to randomize',
        );
        return this._shuffle(validLeaders.slice(1)).slice(
          0,
          playerInfos.length,
        );
      })
      .with(false, () => {
        invariant(validLeaders.length > 0, 'missing leader');
        return new Array(playerInfos.length).fill(validLeaders[0]);
      })
      .exhaustive();
    // Spawn all leader cards first (so SPAWN_CARDS events don't break log grouping)
    const leaderCards = this._spawnCards(orderedLeaders);

    // Then emit ASSIGN_LEADER + GAIN_CARD for each player
    this.computedState.playerStates.forEach((playerState, i) => {
      const card = leaderCards[i];

      // Emit ASSIGN_LEADER first (replaces PURCHASE_CARD's role for leaders)
      this._addEvents([
        {
          type: EventTypes.ASSIGN_LEADER,
          payload: {
            userID: playerState.userID,
            cardID: card.id,
          },
        },
      ]);

      // Then emit GAIN_CARD (same as normal card acquisition)
      const context = this._makeContext(playerState.userID);
      const { events } = Rules.handleGainCard(context, card);
      this._addEvents(events);
    });

    // TODO: debug starting cards

    // tribute deck
    const tributeCards = this._spawnTributeCards(
      Object.values(this.staticState.tributeCardDefinitionsByID),
    );
    this._addEvents([
      {
        type: EventTypes.SETUP_TRIBUTE_DECK,
        payload: {
          cardIDs: tributeCards.map((card) => card.id),
        },
      },
    ]);

    // setup age
    this.setUpAge(1);

    this.progressIfPossible();

    this.updateReadyByUserID();
  }

  private setUpAge(age: number): void {
    // Reshuffle tributes at age transitions if option enabled
    if (age > 1 && this.staticState.options.reshuffleTributesEachAge) {
      this._addEvents([{ type: EventTypes.TRIBUTES_RESHUFFLED, payload: {} }]);
    }

    const cardDefs = Object.values(this.staticState.cardDefinitionsByID).filter(
      (card_def) =>
        card_def.age === age && card_def.affinity !== CardAffinity.Basic,
    );
    const cards = this._spawnCards(cardDefs);

    const basicCardDefs = Object.values(
      this.staticState.cardDefinitionsByID,
    ).filter(
      (card_def) =>
        card_def.age === age && card_def.affinity === CardAffinity.Basic,
    );
    const basicCards = this._spawnCards(basicCardDefs);
    this._addEvents([
      {
        type: EventTypes.SETUP_DECK,
        payload: {
          cardIDs: cards.map((card) => card.id),
        },
      },
    ]);
    this._addEvents([
      {
        type: EventTypes.SETUP_BASIC_PILE,
        payload: {
          cardIDs: basicCards.map((card) => card.id),
        },
      },
    ]);

    this._addEvents([
      {
        type: EventTypes.SET_UP_AGE,
        payload: {
          age,
        },
      },
    ]);

    if (this.staticState.options.tutorial) {
      const tutorialState = nullthrows(this.secretState.tutorialState);
      tutorialState.nextCardIndex = 0;
    }

    this.setUpTurn(1);
  }

  private finishAge(): void {
    this._addEvents(
      this.computedState.table.map((card) => {
        return {
          type: EventTypes.DISCARD_CARD,
          payload: {
            cardID: card.id,
          },
        };
      }),
    );

    this._addEvents(
      this.computedState.tributeRow.map((card) => {
        return {
          type: EventTypes.DISCARD_TRIBUTE,
          payload: { cardID: card.id },
        };
      }),
    );

    this._addEvents([
      {
        type: EventTypes.END_OF_AGE,
        payload: {},
      },
    ]);
  }

  private setUpTurn(turn: number): void {
    this._addEvents([
      {
        type: EventTypes.SET_UP_TURN,
        payload: {
          turn,
        },
      },
    ]);

    this.setUpPhase(Phase.SETUP);
  }

  private finishTurn(): void {
    this.secretState.bidsByPlayerID = {};
    this.secretState.resolvesByPlayerID = {};

    if (this.staticState.options.wipeTradeRow) {
      this.computedState.table.forEach((card) => {
        this._addEvents([
          {
            type: EventTypes.DISCARD_CARD,
            payload: {
              cardID: card.id,
            },
          },
        ]);
      });
    }

    if (this.staticState.options.wipeTributeRow) {
      this.computedState.tributeRow.forEach((card) => {
        this._addEvents([
          {
            type: EventTypes.DISCARD_TRIBUTE,
            payload: {
              cardID: card.id,
            },
          },
        ]);
      });
    }

    // this._addEvents([
    //   {
    //     type: EventTypes2.END_OF_TURN,
    //     payload: {},
    //   },
    // ]);
  }

  private setUpPhase(phase: Phase): void {
    this.serverState.phaseStartTimestamp = Date.now();
    this.serverState.phaseThinkingMillisByPlayerID = {};

    this.staticState.playerInfos.forEach((playerInfo) => {
      this.serverState.phaseThinkingMillisByPlayerID[playerInfo.userID] = 0;
    });

    this._addEvents([
      {
        type: EventTypes.SET_UP_PHASE,
        payload: {
          phase,
        },
      },
    ]);
  }

  private finishPhase(): void {
    this.staticState.playerInfos.forEach((playerInfo) => {
      this.serverState.totalThinkingMillisByPlayerID[playerInfo.userID] +=
        this.serverState.phaseThinkingMillisByPlayerID[playerInfo.userID];
    });

    this._addEvents([
      {
        type: EventTypes.END_OF_PHASE,
        payload: {
          elapsedPhaseMillisByPlayerID:
            this.serverState.phaseThinkingMillisByPlayerID,
        },
      },
    ]);
  }

  private progressIfPossible(): void {
    if (this.serverState.gameEndTimestamp) {
      return;
    }

    if (this._heldPhase == this.computedState.phase) {
      this._heldPhase = null;
      return;
    }

    const finished = match(this.computedState.phase)
      .with(Phase.SETUP, () => {
        return this.handleSetupPhase();
      })
      .with(Phase.PRODUCTION, () => {
        return this.handleProductionPhase();
      })
      .with(Phase.PLANNING, () => {
        return this.handlePlanningPhase();
      })
      .with(Phase.WAR, () => {
        return this.handleWarPhase();
      })
      .with(Phase.RESOLUTION, () => {
        return this.handleResolutionPhase();
      })
      .with(Phase.TRIBUTE, () => {
        return this.handleTributePhase();
      })
      .exhaustive();

    if (!finished) {
      return;
    }

    this.finishPhase();
    const newPhase = nextPhase(this.computedState.phase);
    if (newPhase != PhaseOrder[0]) {
      this.setUpPhase(newPhase);
      return this.progressIfPossible();
    }

    this.finishTurn();
    const newTurn = this.computedState.turn + 1;
    if (newTurn <= this.staticState.turnsPerAge) {
      this.setUpTurn(newTurn);
      return this.progressIfPossible();
    }

    this.finishAge();
    const newAge = this.computedState.age + 1;
    if (newAge <= GameConstants.AGES_PER_GAME) {
      this.setUpAge(newAge);
      return this.progressIfPossible();
    } else {
      this.finishGame();
    }
  }

  private finishGame(): void {
    this.serverState.gameEndTimestamp = Date.now();

    this.staticState.playerInfos.forEach((playerInfo) => {
      let context = this._makeContext(playerInfo.userID);
      context = Rules.handleEndOfGameEffects(context);

      this._addEvents([
        {
          type: EventTypes.END_GAME_EFFECTS,
          payload: {
            userID: playerInfo.userID,
            counters: extractCounterDeltaFromContext(context),

            triggeredCards: context.triggeredCards,
          },
        },
      ]);
    });

    const maxFavor = max(
      this.computedState.playerStates.map((playerState) => {
        return playerState.counters.favor;
      }),
    );
    const winningUserIDs = this.computedState.playerStates
      .filter((playerState) => {
        return playerState.counters.favor === maxFavor;
      })
      .map((playerState) => {
        return playerState.userID;
      });
    const endOfGameFavorByUserID = Object.fromEntries(
      this.computedState.playerStates.map((playerState) => {
        return [playerState.userID, playerState.counters.favor];
      }),
    );

    this._addEvents([
      {
        type: EventTypes.END_OF_GAME,
        payload: {
          winningUserIDs,
          endOfGameFavorByUserID,
        },
      },
    ]);
  }

  private handleSetupPhase(): boolean {
    while (
      this.computedState.table.length < this.staticState.boardSize &&
      this.computedState.deck.length > 0
    ) {
      const dealtCard = match(this.staticState.options.tutorial)
        .with(true, () => {
          const cardOrder = TUTORIAL_CARD_ORDER_BY_AGE[this.computedState.age];
          const tutorialState = nullthrows(this.secretState.tutorialState);
          const nextCardIndex = tutorialState.nextCardIndex;

          if (nextCardIndex >= cardOrder.length) {
            return this._randomSelect(this.computedState.deck);
          }

          const cardDefID = cardOrder[nextCardIndex];
          const card = this.computedState.deck.find(
            (card) => card.defID === cardDefID,
          );
          invariant(card, 'Tutorial card %s not found', cardDefID);

          tutorialState.nextCardIndex++;
          return card;
        })
        .with(false, () => {
          return this._randomSelect(this.computedState.deck);
        })
        .exhaustive();

      this._addEvents([
        {
          type: EventTypes.DEAL_CARD,
          payload: {
            cardID: dealtCard.id,
          },
        },
      ]);
    }

    while (
      this.computedState.tributeRow.length < this.staticState.tributeRowSize &&
      this.computedState.tributeDeck.length +
        this.computedState.tributeDiscard.length >
        0
    ) {
      if (this.computedState.tributeDeck.length === 0) {
        this._addEvents([
          {
            type: EventTypes.TRIBUTES_RESHUFFLED,
            payload: {},
          },
        ]);
      }
      invariant(
        this.computedState.tributeDeck.length > 0,
        'tribute deck is empty',
      );

      const dealtTribute = match(this.staticState.options.tutorial)
        .with(true, () => {
          const tributeOrder = TUTORIAL_TRIBUTE_ORDER;
          const tutorialState = nullthrows(this.secretState.tutorialState);
          const nextTributeIndex = tutorialState.nextTributeIndex;

          if (nextTributeIndex >= tributeOrder.length) {
            return this._randomSelect(this.computedState.tributeDeck);
          }

          tutorialState.nextTributeIndex += 1;

          const tributeDefID = tributeOrder[nextTributeIndex];
          const tribute = this.computedState.tributeDeck.find(
            (tribute) => tribute.defID === tributeDefID,
          );

          invariant(tribute, 'Tutorial tribute %s not found', tributeDefID);
          return tribute;
        })
        .with(false, () => {
          return this._randomSelect(this.computedState.tributeDeck);
        })
        .exhaustive();

      this._addEvents([
        {
          type: EventTypes.DEAL_TRIBUTE,
          payload: {
            cardID: dealtTribute.id,
          },
        },
      ]);
    }

    return true;
  }

  private handleProductionPhase(): boolean {
    this.staticState.playerInfos.forEach((playerInfo) => {
      let context = this._makeContext(playerInfo.userID);
      context = Rules.handleProduction(context);

      this._addEvents([
        {
          type: EventTypes.PRODUCTION,
          payload: {
            userID: playerInfo.userID,
            counters: extractCounterDeltaFromContext(context),
          },
        },
      ]);
    });

    const planningStateByTributeID: {
      [tributeID: string]: any;
    } = {};
    this.computedState.tributeRow.forEach((tribute) => {
      const tributeLogic = getTributeLogic(tribute);
      const data = tributeLogic?.planningState(this.computedState);
      if (data !== undefined && data !== null) {
        planningStateByTributeID[tribute.id] = data;
      }
    });

    this._addEvents([
      {
        type: EventTypes.TRIBUTE_PLANNING_DATA,
        payload: {
          planningStateByTributeID,
        },
      },
    ]);

    return true;
  }

  private handlePlanningPhase(): boolean {
    const allReady = this.staticState.playerInfos.every((playerInfo) => {
      return this.secretState.bidsByPlayerID[playerInfo.userID];
    });
    if (!allReady) {
      return false;
    }

    const planningEvents: GameEventData[] = [];
    this.staticState.playerInfos.forEach((playerInfo) => {
      const bid = this.secretState.bidsByPlayerID[playerInfo.userID];
      const card = this.computedState.table[bid.tradeRowIndex];

      const { context, events } = Rules.handleBid(
        this._makeContext(playerInfo.userID),
        bid,
        this.computedState,
      );

      planningEvents.push(...events);
    });
    this._addEvents(planningEvents);

    return true;
  }

  private handleWarPhase(): boolean {
    const noDiceType = this.staticState.options.noDice ?? NoDiceType.Standard;
    const skipInitialDiceRolls =
      noDiceType === NoDiceType.NoDice || noDiceType === NoDiceType.NoDicePlus;

    // prepare data
    const playerWarInfosByTradeRowIndex: Map<number, PlayerWarInfo[]> =
      new Map();
    this.staticState.playerInfos.forEach((playerInfo) => {
      const playerState = getPlayerState(this.computedState, playerInfo.userID);
      const bid = nullthrows(playerState.bid);
      const totalBonus = bid.military;

      // In NoDice modes, skip initial dice rolls (use military only)
      let rolls: number[] = [];
      if (!skipInitialDiceRolls) {
        rolls = times(this.computedState.age, () => this._rollDie());

        if (this.staticState.options.tutorial) {
          const tutorialRolls =
            playerInfo.userID === 'dummy0'
              ? getTutorialData(this.computedState.age, this.computedState.turn)
                  ?.botRolls
              : getTutorialData(this.computedState.age, this.computedState.turn)
                  ?.playerRolls;
          if (tutorialRolls) {
            rolls = tutorialRolls;
          }
        }
      }

      const v = playerWarInfosByTradeRowIndex.get(bid.tradeRowIndex) || [];
      v.push({
        userID: playerInfo.userID,
        rolls,
        totalBonus,
        totalFinal: Utility.sum(rolls, identity) + totalBonus,
      });
      playerWarInfosByTradeRowIndex.set(bid.tradeRowIndex, v);
    });

    // resolve ties and calculate war results
    playerWarInfosByTradeRowIndex.forEach((playerBidInfos, tradeRowIndex) => {
      const card = this.computedState.table[tradeRowIndex];

      const clashResults = this.computeClashResults(
        card,
        playerBidInfos,
        noDiceType,
      );

      this._addEvents([
        {
          type: EventTypes.CONFLICT_RESULTS,
          payload: clashResults,
        },
      ]);

      if (
        this.staticState.options.retreat1 ||
        this.staticState.options.retreat2 ||
        this.staticState.options.retreat3
      ) {
        playerWarInfosByTradeRowIndex.forEach((playerInfos, tradeRowIndex) => {
          const winningPlayerInfo = maxBy(playerInfos, (x) => x.totalFinal)!;
          playerInfos.forEach((playerInfo) => {
            if (playerInfo.userID !== winningPlayerInfo.userID) {
              let context = this._makeContext(playerInfo.userID);
              if (this.staticState.options.retreat1) {
                context = Rules.handlePartialMilitaryRefund(context);
              } else if (this.staticState.options.retreat2) {
                context = Rules.handleFullMilitaryRefund(context);
              } else if (this.staticState.options.retreat3) {
                context = Rules.handleHigherBidMilitaryRefund(
                  context,
                  winningPlayerInfo.totalBonus,
                );
              }
              if (context.refund_delta.military > 0) {
                this._addEvents([
                  {
                    type: EventTypes.REFUND_MILITARY,
                    payload: {
                      userID: playerInfo.userID,
                      refundedMilitary: context.refund_delta.military,
                    },
                  },
                ]);
              }
            }
          });
        });
      }

      if (this.staticState.options.warTokens) {
        playerWarInfosByTradeRowIndex.forEach((playerInfos, tradeRowIndex) => {
          if (playerInfos.length < 2) {
            return;
          }
          playerInfos.forEach((playerInfo) => {
            const events = Rules.handleTokenGain(
              this._makeContext(playerInfo.userID),
              Token.War,
            );
            this._addEvents(events);
          });
        });
      }
    });

    return true;
  }

  private handleResolutionPhase(): boolean {
    const allReady = this.staticState.playerInfos.every((playerInfo) => {
      return this.secretState.resolvesByPlayerID[playerInfo.userID];
    });
    if (!allReady) {
      return false;
    }

    // XXX(ldg): this is for resolutionData
    const initialComputedState = this.computedState;

    this.staticState.playerInfos.forEach((playerInfo) => {
      const playerState = getPlayerState(this.computedState, playerInfo.userID);
      const bid = nullthrows(playerState.bid);
      const resolve = this.secretState.resolvesByPlayerID[playerInfo.userID];

      // TODO(ldg) compute initial prophecy favor if necessary

      const draftedCard = this.computedState.table[bid.tradeRowIndex];
      const cardToGain = nullthrows(
        this.computedState.cardsByID[resolve.cardIDToGain],
      );

      const isBasic = this.computedState.basicPile.some(
        (card) => card.id === cardToGain.id,
      );
      let gainedCard: Rules.CardWithID = isBasic
        ? this._spawnCards([cardToGain])[0]
        : cardToGain;

      let { events: purchaseEvents } = Rules.handleResolutionSelection(
        this._makeContext(playerInfo.userID),
        draftedCard,
        gainedCard,
      );
      this._addEvents(purchaseEvents);

      let gainContext = this._makeContext(playerInfo.userID);
      const { events } = Rules.handleGainCard(gainContext, gainedCard);
      this._addEvents(events);
    });

    return true;
  }

  private handleTributePhase(): boolean {
    const tributeIDsToRemove = new Set<string>();

    const turnEvents = getCurrentTurnEvents(this.computedState);

    this.staticState.playerInfos.forEach((playerInfo) => {
      // handle completed tributes
      const {
        context: completed_tribute_context,
        events: completed_tribute_events,
      } = Rules.handleTributeGain(
        this._makeContext(playerInfo.userID),
        this.computedState.tributeRow,
        this.computedState,
        turnEvents,
      );
      completed_tribute_context.completedTributeIDs.forEach((tribute_id) => {
        tributeIDsToRemove.add(tribute_id);
      });

      this._addEvents(completed_tribute_events);
    });

    this._addEvents(
      Array.from(tributeIDsToRemove).map((tributeID) => {
        return {
          type: EventTypes.DISCARD_TRIBUTE,
          payload: {
            cardID: tributeID,
          },
        };
      }),
    );

    return true;
  }

  private computeClashResults(
    card: Rules.CardWithID,
    playerInfos: PlayerWarInfo[],
    noDiceType: NoDiceType,
  ): ClashResults {
    invariant(
      playerInfos.length >= 1,
      'must have at least one player per conflicted card',
    );

    let rounds: ConflictRoundInfo[] = [];
    let currentPlayerInfos = [...playerInfos];
    while (true) {
      let bestInfos: PlayerWarInfo[] = [];
      let resultsByUserID: ConflictRoundInfo = {};
      currentPlayerInfos.forEach((playerInfo) => {
        const result = {
          playerID: playerInfo.userID,
          rolls: playerInfo.rolls,
          bonus: playerInfo.totalBonus,
          total: playerInfo.totalFinal,
        };
        resultsByUserID[playerInfo.userID] = result;
        var best = bestInfos[0];
        if (!best || playerInfo.totalFinal > best.totalFinal) {
          bestInfos = [playerInfo];
        } else if (best && playerInfo.totalFinal === best.totalFinal) {
          bestInfos.push(playerInfo);
        }
      });
      rounds.push({ ...resultsByUserID });
      invariant(bestInfos.length >= 1, 'must have at least one best result');

      currentPlayerInfos = bestInfos;
      if (currentPlayerInfos.length !== 1) {
        // In NoDicePlus mode, ties result in no winner (all tied players lose)
        if (noDiceType === NoDiceType.NoDicePlus) {
          return {
            cardID: card.id,
            rounds: rounds,
            winningPlayerID: null,
          };
        }
        // reroll to break ties
        currentPlayerInfos.forEach((playerInfo) => {
          playerInfo.rolls = times(this.computedState.age, () =>
            this._rollDie(),
          );
          playerInfo.totalFinal =
            Utility.sum(playerInfo.rolls, identity) + playerInfo.totalBonus;
        });
      } else {
        // done
        break;
      }
    }

    invariant(currentPlayerInfos.length === 1, 'must have exactly one winner');
    const winningUserID = currentPlayerInfos[0].userID;

    return {
      cardID: card.id,
      rounds: rounds,
      winningPlayerID: winningUserID,
    };
  }

  private _spawnCards(cardDefs: CardDef[]): Rules.CardWithID[] {
    const cardIDDefIDPairs: [string, string][] = cardDefs.map((card_def) => {
      const cardID = `${this.secretState.nextCardID}`;
      this.secretState.nextCardID += 1;
      return [cardID, card_def.defID];
    });

    this._addEvents([
      {
        type: EventTypes.SPAWN_CARDS,
        payload: {
          cardIDDefIDPairs,
        },
      },
    ]);

    return cardIDDefIDPairs.map(([cardID]) => {
      return nullthrows(this.computedState.cardsByID[cardID]);
    });
  }

  private _spawnTributeCards(
    cardDefs: TributeDef[],
  ): Rules.TributeCardWithID[] {
    const cardIDDefIDPairs: [string, string][] = cardDefs.map((card_def) => {
      const cardID = `${this.secretState.nextCardID}`;
      this.secretState.nextCardID += 1;
      return [cardID, card_def.defID];
    });

    this._addEvents([
      {
        type: EventTypes.SPAWN_TRIBUTES,
        payload: {
          cardIDDefIDPairs,
        },
      },
    ]);

    return cardIDDefIDPairs.map(([cardID]) => {
      return nullthrows(this.computedState.tributeCardsByID[cardID]);
    });
  }

  private _addEvents(eventDatas: GameEventData[]): void {
    if (eventDatas.length === 0) {
      return;
    }

    const now = Date.now();
    const events = eventDatas.map((eventData) => {
      return {
        ...eventData,
        timestamp: now,
      };
    });

    this.serverState.events.push(...events);

    this.computedState = applyGameEvents(this.computedState, events);
  }

  _makeContext(playerID: string): Rules.Context {
    return Rules.makeContext(
      getPlayerState(this.computedState, playerID),
      this.computedState.turn,
      this.computedState.age,
      this.staticState.options,
    );
  }
}

export function extractCounterDeltaFromContext(
  context: Rules.Context,
): Partial<Utility.CounterDelta> {
  const initialCounters = context.player.counters;

  let finalCounters = context.player.counters;
  finalCounters = Utility.subtractCounterDeltas(
    finalCounters,
    context.spent_delta,
  );
  finalCounters = Utility.addCounters(finalCounters, context.production_delta);

  const { maxGold, maxMilitary } = getMaxCounters(context);
  finalCounters.gold = Utility.clamp(finalCounters.gold, 0, maxGold);
  finalCounters.military = Utility.clamp(
    finalCounters.military,
    0,
    maxMilitary,
  );

  return Utility.compressCounterDelta(
    Utility.subtractCounterDeltas(finalCounters, initialCounters),
  );
}
